import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ProjectReader, Reader, ReaderDraft, ReaderField } from '../lib/types';
import { projectDoc } from './projects';

/**
 * Readers: what one person knows how to read.
 *
 * The app shipped with two instruments hardwired into it — a Zetasizer giving
 * size, PDI and zeta, and an encapsulation assay giving EE. That covered the
 * lab that commissioned it and nothing else. A plate reader produces whatever
 * the person who set the protocol decided it would, under whatever headings
 * they chose, and no fixed list of four fields can meet that.
 *
 * A reader is that list, made data instead of code: a name, and the fields it
 * produces. The extraction prompt, the confirmation screen and the export are
 * all generated from it, so adding an instrument is something a user does on a
 * Tuesday afternoon rather than something that needs a deploy.
 *
 * Readers belong to a person, not a project.
 *
 * They were briefly stored on the project and shared with everyone on it, which
 * was wrong in both directions: joining a project silently changed what your
 * capture screen offered, and defining a reader for your own bench pushed it
 * into someone else's app. A reader encodes how *you* run an instrument, which
 * is not something to inherit by being added to a study.
 *
 * What is shared is the data. Every capture carries a snapshot of the reader
 * that read it, so a colleague opening your results sees the fields, the units
 * and where each number came from — they simply cannot capture with your reader
 * without defining their own.
 */

export const readersCol = (uid: string) => collection(db, 'users', uid, 'readers');

export const readerDoc = (uid: string, id: string) => doc(db, 'users', uid, 'readers', id);

export function watchReaders(uid: string, cb: (r: Reader[]) => void) {
  const q = query(readersCol(uid), orderBy('name'));
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Reader)),
  );
}

/**
 * A stable key for a field, derived from its label.
 *
 * Stored data is keyed by this, so it must not change when someone corrects a
 * label's capitalisation or adds a unit to it. Generated once, on creation, and
 * carried forward on every edit.
 */
export function fieldKeyFrom(label: string, taken: string[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'field';
  if (!taken.includes(base)) return base;
  for (let n = 2; n < 100; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

/** Blank field, ready to be filled in by the builder. */
export function emptyField(): ReaderField {
  return { key: '', label: '', unit: '', decimals: 2, min: null, max: null, aliases: [] };
}

export function emptyReaderDraft(): ReaderDraft {
  return { name: '', description: '', fields: [emptyField()] };
}

/** Assign keys to any field that does not have one yet, leaving existing keys alone. */
function withKeys(fields: ReaderField[]): ReaderField[] {
  const taken = fields.map((f) => f.key).filter(Boolean);
  return fields.map((f) => {
    if (f.key) return f;
    const key = fieldKeyFrom(f.label, taken);
    taken.push(key);
    return { ...f, key };
  });
}

export function cleanDraft(
  draft: ReaderDraft,
): Omit<Reader, 'id' | 'builtIn' | 'createdAt' | 'updatedAt'> {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    fields: withKeys(
      draft.fields
        .filter((f) => f.label.trim())
        .map((f) => ({
          ...f,
          label: f.label.trim(),
          unit: f.unit.trim(),
          aliases: f.aliases.map((a) => a.trim()).filter(Boolean),
        })),
    ),
  };
}

export async function createReader(uid: string, draft: ReaderDraft): Promise<string> {
  const ref = await addDoc(readersCol(uid), {
    ...cleanDraft(draft),
    builtIn: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** An id for a reader not yet saved, so a trial photo can be filed under it. */
export function newReaderId(uid: string): string {
  return doc(readersCol(uid)).id;
}

/** Save a reader learned from a trial photo, under the id it was learned with. */
export async function saveLearnedReader(uid: string, reader: ProjectReader) {
  await setDoc(readerDoc(uid, reader.id), {
    name: reader.name,
    description: reader.description ?? '',
    fields: reader.fields,
    builtIn: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateReader(uid: string, id: string, draft: ReaderDraft) {
  await updateDoc(readerDoc(uid, id), { ...cleanDraft(draft), updatedAt: serverTimestamp() });
}

export async function deleteReader(uid: string, id: string) {
  await deleteDoc(readerDoc(uid, id));
}

/**
 * What a capture keeps about the reader that read it.
 *
 * Stored on the capture rather than looked up, because the reader belongs to
 * whoever made the capture and nobody else can read their collection — and
 * because a reader edited next year must not silently restate what last year's
 * numbers meant.
 */
export function readerSnapshot(reader: Pick<Reader, 'id' | 'name' | 'fields'>) {
  return {
    id: reader.id,
    name: reader.name,
    fields: reader.fields.map((f) => ({
      key: f.key,
      label: f.label,
      unit: f.unit,
      decimals: f.decimals,
    })),
  };
}

/**
 * The two instruments the app used to hardcode, as ordinary readers.
 *
 * Seeded per person, with the ids the old code used, so captures already
 * recorded against "zetasizer" or "ribogreen" still point at something real.
 * They are marked builtIn only so the UI can explain where they came from —
 * they are editable and deletable like any other, which is the whole point.
 */
const BUILT_INS: { id: string; reader: Omit<Reader, 'id' | 'createdAt' | 'updatedAt'> }[] = [
  {
    id: 'zetasizer',
    reader: {
      name: 'Zetasizer',
      description: 'Dynamic light scattering — size, PDI and zeta potential in one pass.',
      builtIn: true,
      fields: [
        {
          key: 'size',
          label: 'Z-average size',
          unit: 'nm',
          decimals: 1,
          min: 20,
          max: 1000,
          aliases: ['Z-Avg', 'Z-Average', 'Z-Ave', 'Size', 'Diameter', 'Dh', 'd.nm'],
        },
        {
          key: 'pdi',
          label: 'PDI',
          unit: '',
          decimals: 3,
          min: 0,
          max: 1,
          aliases: ['PdI', 'Polydispersity', 'Dispersity', 'Width'],
        },
        {
          key: 'zeta',
          label: 'Zeta potential',
          unit: 'mV',
          decimals: 2,
          min: -100,
          max: 100,
          aliases: ['Zeta', 'ZP', 'Z-Potential', 'Surface Charge'],
        },
      ],
    },
  },
  {
    id: 'ribogreen',
    reader: {
      name: 'Encapsulation',
      description: 'Encapsulation efficiency, already computed as a percentage.',
      builtIn: true,
      fields: [
        {
          key: 'ee',
          label: 'Encapsulation efficiency',
          unit: '%',
          decimals: 2,
          min: 0,
          max: 100,
          aliases: ['EE', 'EE%', '%EE', 'Encapsulation', 'Entrapment', 'Loading Efficiency'],
        },
      ],
    },
  },
];

/**
 * Put the built-ins in place the first time a project's readers are opened.
 *
 * Written with `setDoc` at fixed ids and only when the collection is empty, so
 * running twice is harmless and a user who has since edited or deleted one does
 * not find it resurrected.
 */
export async function ensureBuiltInReaders(uid: string) {
  const existing = await getDocs(readersCol(uid));
  if (!existing.empty) return;
  await Promise.all(
    BUILT_INS.map((b) =>
      setDoc(readerDoc(uid, b.id), {
        ...b.reader,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    ),
  );
}

/**
 * The readers a new project can start from without anyone defining anything.
 *
 * Offered, never imposed: a project starts with none, and its owner picks these,
 * their own, or builds one. The ids match the ones the app has always used, so
 * readings keyed to them stay recognisable.
 */
export const READER_TEMPLATES: ProjectReader[] = BUILT_INS.map((b) => ({
  id: b.id,
  name: b.reader.name,
  description: b.reader.description,
  fields: b.reader.fields,
  builtIn: true,
}));

export function toProjectReader(r: Reader): ProjectReader {
  return { id: r.id, name: r.name, description: r.description, fields: r.fields };
}

/**
 * Replace a project's reader list. Only the owner can, by the project rules.
 *
 * Written whole rather than with arrayUnion/arrayRemove: those compare entries
 * by deep equality, so editing one field of a reader would leave both the old
 * and the new copy behind.
 */
export async function setProjectReaders(projectId: string, readers: ProjectReader[]) {
  await updateDoc(projectDoc(projectId), {
    readers: readers.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description ?? '',
      fields: r.fields,
      builtIn: r.builtIn === true,
    })),
    updatedAt: serverTimestamp(),
  });
}

